/**
 * Acquires the Fabric Dual-Token (subjectToken) via the Workload Client SDK.
 *
 * Per the WDK docs, calling acquireAccessToken({}) (no parameters) returns a
 * token whose audience is the workload's own backend Entra app. We forward
 * that token to /api/* and the broker exchanges it via OBO for a Fabric token.
 *
 * In dev, set VITE_DEV_TOKEN to bypass the SDK.
 */
import { createExtensionClient, type ExtensionClientAPI } from "@ms-fabric/workload-client";

let client: ExtensionClientAPI | null = null;
function getClient(): ExtensionClientAPI {
  client ??= createExtensionClient();
  return client;
}

export async function getSubjectToken(): Promise<string> {
  const dev = (import.meta as any).env?.VITE_DEV_TOKEN as string | undefined;
  if (dev) return dev;

  try {
    const { token } = await getClient().auth.acquireAccessToken({});
    return token;
  } catch (e: any) {
    // The Workload Client SDK rejects with a structured object whose `error`
    // value maps to ExtensionAuthError (0=Unsupported, 1=UserInteractionFailed,
    // 2=WorkloadConfigError, 3=Unknown). 2 means Fabric's metadata for this
    // workload is out of sync with the Entra app: the .nupkg in the Admin
    // Portal must be re-published so Fabric re-reads the FE app configuration.
    const code: number | undefined = typeof e === "object" && e !== null ? (e as any).error : undefined;
    const map: Record<number, string> = {
      0: "acquireAccessToken not supported in this Fabric environment",
      1: "User cancelled or failed the consent dialog",
      2: "Fabric workload configuration is out of sync with the Entra app. Re-publish the workload .nupkg in the Fabric Admin Portal → Workload Hub.",
      3: "Unknown auth error"
    };
    const msg = code !== undefined && map[code] ? map[code] : `acquireAccessToken failed (${JSON.stringify(e)})`;
    throw new Error(msg);
  }
}
